import { Injectable, computed, inject, signal } from '@angular/core';
import { OrderDetailPayload } from '@features/orders/models/order-detail.model';
import { OrderDetailService } from '@features/orders/services/order-detail.service';
import {
  injectMutation,
  injectQuery,
  injectQueryClient,
  queryOptions,
} from '@ngneat/query';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import { derivedAsync } from 'ngxtension/derived-async';
import { sleep } from 'radash';
import {
  OrderCancellationPayload,
  OrderCancellationResponse,
} from '../models/order-cancellation.model';
import { OrderCancelDataService } from '../services/order-cancel-data.service';

@Injectable({ providedIn: 'root' })
export class OrderCancelState {
  private readonly orderCancelDataService = inject(OrderCancelDataService);
  private readonly orderDetailService = inject(OrderDetailService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly query = injectQuery();
  private readonly queryClient = injectQueryClient();
  private readonly mutation = injectMutation();

  readonly orderId = signal<string | null>(null);
  readonly orderType = signal<string | null>(null);

  private getCancellationTypesQueryOptions(tenantId: string) {
    return queryOptions({
      queryKey: ['cancellation-types', tenantId] as const,
      queryFn: () =>
        this.orderCancelDataService.fetchCancellationTypes(tenantId),
    });
  }

  private getOrderDetailQueryOptions(payload: OrderDetailPayload) {
    return queryOptions({
      queryKey: [
        'order-detail',
        payload.tenantId,
        payload.orderId,
        payload.orderType,
      ] as const,
      queryFn: () => this.orderDetailService.getOrderDetail(payload),
      staleTime: 1000 * 60 * 3,
    });
  }

  readonly cancellationTypesQuery = derivedAsync(() => {
    const tenantId = this.tenantUrlUtil.normalizedTenant();

    return this.query({
      ...this.getCancellationTypesQueryOptions(tenantId!),
      enabled: !!tenantId,
      retry: false,
    }).result$;
  });

  readonly orderDetailQuery = derivedAsync(() => {
    const tenantId = this.tenantUrlUtil.normalizedTenant();
    const orderId = this.orderId();
    const orderType = this.orderType();
    return this.query({
      ...this.getOrderDetailQueryOptions({
        tenantId: tenantId!,
        orderId: orderId!,
        orderType: orderType!,
      }),
      enabled: !!tenantId && !!orderId && !!orderType,
    }).result$;
  });

  readonly isLoading = computed(() => {
    const typesQuery = this.cancellationTypesQuery();
    const detailQuery = this.orderDetailQuery();

    const typesLoading = typesQuery?.isLoading ?? false;
    const detailLoading = detailQuery?.isLoading ?? false;

    return typesLoading || detailLoading;
  });

  readonly cancelError = computed(() => {
    const typesQuery = this.cancellationTypesQuery();
    return typesQuery?.error ?? null;
  });

  readonly orderDetailError = computed(() => {
    const detailQuery = this.orderDetailQuery();
    return detailQuery?.error ?? null;
  });

  readonly hasError = computed(() => {
    return !!this.cancelError() || !!this.orderDetailError();
  });

  readonly cancellationTypes = computed(() => {
    const query = this.cancellationTypesQuery();
    return query?.data ?? [];
  });

  readonly orderDetail = computed(() => {
    const query = this.orderDetailQuery();
    return query?.data ?? null;
  });

  readonly isLoaded = computed(() => {
    const cancelTypesReady =
      !this.isLoading() &&
      !this.hasError() &&
      this.cancellationTypes().length > 0;

    const detailReady =
      !this.isLoading() &&
      !this.orderDetailError() &&
      this.orderDetail() !== null;

    return cancelTypesReady && detailReady;
  });

  readonly isAllowedToCancel = computed(() => {
    const detail = this.orderDetail();
    const detailQuery = this.orderDetailQuery();
    const isDetailLoaded = detailQuery?.isSuccess ?? false;
    const hasDetailError = !!this.orderDetailError();
    
    if (!isDetailLoaded || hasDetailError || !detail) return false;
    
    const cancellationType = detail.options?.cancellationType;
    if (cancellationType === 'NONE') return false;
    
    return true;
  });

  readonly cancelOrderMutation = this.mutation({
    mutationFn: async (payload: OrderCancellationPayload) => {
      return await this.orderCancelDataService.cancelOrder(payload);
    },
    onSuccess: async (
      response: OrderCancellationResponse,
      payload: OrderCancellationPayload
    ) => {
      if (response.code === 1) {
        await sleep(2000);
        await this.queryClient.invalidateQueries({
          queryKey: ['order-detail'],
          exact: false,
        });

        await this.queryClient.invalidateQueries({
          queryKey: ['order-timeline'],
          exact: false,
        });

        await this.queryClient.refetchQueries({
          queryKey: ['order-detail'],
          exact: false,
        });
      }
    },
  });

  async refetchCancellationTypes() {
    const tenantId = this.tenantUrlUtil.normalizedTenant();
    if (!tenantId) return;

    await this.queryClient.refetchQueries({
      queryKey: ['cancellation-types', tenantId],
      exact: true,
    });
  }

  resetMutationState() {
    this.cancelOrderMutation.reset();
  }
}
